import { useMutation, UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { NonEmptyItemOverwrite, useApi } from '@tymely/api';
import { PartialBy } from '@global/types';
import { IGrPolicy, IOrganization, IPolicySet, IPolicyVersion } from '@tymely/atoms';
import usePolicyParams from '@tymely/pages/GraphPolicyEditor/hooks/usePolicyParams';
import { useEffect } from 'react';

const GR_FETCH_POLICY_QUERY_KEY = 'graph-policy';
const GR_SEARCH_POLICY_QUERY_KEY = 'graph-policy-search';
const GR_POLICY_VERSIONS_QUERY_KEY = 'graph-policy-versions';
const GR_POLICY_COPY_MUTATION_KEY = 'graph-policy-copy';
const POLICY_ENDPOINT = 'policy';

export const usePolicyByVersionId = (versionId?: number) => {
    const api = useApi();
    return useQuery({
        queryKey: ['policy', versionId],
        queryFn: () => (versionId ? api.get(`policy/${versionId}`) : Promise.reject('No versionId')),
        enabled: !!versionId,
    });
};

export const useSearchPolicyQuery = (orgId: IOrganization['id'], intentId: IPolicySet['id'], enabled = true) => {
    const api = useApi();
    const queryClient = useQueryClient();

    const query = useQuery<IGrPolicy, AxiosError>({
        queryKey: [GR_SEARCH_POLICY_QUERY_KEY, orgId, intentId],
        queryFn: () =>
            api.get('policies/search', {
                params: {
                    organization_id: orgId,
                    policy_set_id: intentId,
                },
            }),
        enabled: Boolean(orgId && intentId) && enabled,
        staleTime: Infinity,
    });

    const { data: policy } = query;

    useEffect(() => {
        if (policy) {
            queryClient.setQueryData([GR_FETCH_POLICY_QUERY_KEY, policy.id], policy);
        }
    }, [policy]);

    return query;
};

export const useFetchPolicyQuery = () => {
    const { organizationId, intentId, versionId: policyId } = usePolicyParams();
    const api = useApi();

    const query = useQuery<IGrPolicy, AxiosError>({
        queryKey: policyId
            ? [GR_FETCH_POLICY_QUERY_KEY, policyId]
            : [GR_SEARCH_POLICY_QUERY_KEY, organizationId, intentId],
        queryFn: () =>
            policyId
                ? api.get(`policy/${policyId}`)
                : (api.get('policies/search', {
                      params: {
                          organization_id: organizationId,
                          policy_set_id: intentId,
                      },
                  }) as Promise<IGrPolicy>),
        meta: {
            errorMessage: 'failed fetching policy',
        },
        staleTime: Infinity,
        enabled: Boolean((organizationId && intentId) || policyId),
    });

    return query;
};

export const useUpsertGrIntentPolicyMutation = (
    props: {
        onSuccess?: (policy: PartialBy<IGrPolicy, 'id' | 'created_date'>) => void;
        onError?: (error: string[]) => void;
    } = {},
) => {
    const api = useApi();
    const queryClient = useQueryClient();
    const { organizationId, intentId } = usePolicyParams();
    const { onSuccess } = props;
    return useMutation({
        mutationKey: [GR_SEARCH_POLICY_QUERY_KEY],
        mutationFn: async ({
            policy,
            isNewPolicy,
        }: {
            policy: PartialBy<IGrPolicy, 'id' | 'created_date'>;
            isNewPolicy: boolean;
        }) => {
            const endpoint = isNewPolicy ? POLICY_ENDPOINT : `${POLICY_ENDPOINT}/${policy.id}`;
            const policyUpdated = await api.put(endpoint, policy);
            return { ...policy, ...policyUpdated };
        },
        onSuccess: async (data, { policy }) => {
            queryClient.setQueryData([GR_SEARCH_POLICY_QUERY_KEY, organizationId, intentId], () => data);
            return onSuccess?.({ ...policy, ...data });
        },
        onError: (error, { policy }) => {
            if (error instanceof NonEmptyItemOverwrite) {
                props.onError?.([error.message]);
            } else {
                props.onError?.([
                    `Failed saving policy for intent (id=${policy.id}, policy_set_id=${policy.policy_set_id} org_policy_set_id=${policy.org_policy_set_id}): ${
                        (error as any).message
                    }`,
                ]);
            }
        },
    });
};

export const usePolicyVersionsQuery = (orgId: IOrganization['id'], policySetId: IPolicySet['id'], enabled = true) => {
    const api = useApi();
    return useQuery<IPolicyVersion[], AxiosError>({
        queryKey: [GR_POLICY_VERSIONS_QUERY_KEY, orgId, policySetId],
        queryFn: () =>
            api.get('policies/versions', {
                params: {
                    organization_id: orgId,
                    policy_set_id: policySetId,
                },
            }),
        enabled: enabled && !isNaN(orgId) && !isNaN(policySetId),
    });
};

interface CopyPolicyParams {
    source_org_policy_set_id: number;
    source_policy_set_id: number;
    target_org_policy_set_id: number;
    target_policy_set_id: number;
    append_to_target: boolean;
    nodes: string[];
    duplicate_response_templates: boolean;
}

export const useCopyPolicyMutation = (options?: UseMutationOptions<IGrPolicy, AxiosError, CopyPolicyParams>) => {
    const api = useApi();
    const { setQueryData } = useQueryClient();

    return useMutation<IGrPolicy, AxiosError, CopyPolicyParams>({
        mutationKey: [GR_POLICY_COPY_MUTATION_KEY],
        mutationFn: (params: CopyPolicyParams) => api.post('policies/copy', {}, { params }),
        onSuccess: (policy, params, context) => {
            setQueryData([GR_SEARCH_POLICY_QUERY_KEY, policy.org_policy_set_id, policy.policy_set_id], policy);
            setQueryData([GR_FETCH_POLICY_QUERY_KEY, policy.id], () => policy);

            return options?.onSuccess?.(policy, params, context);
        },
        ...options,
    });
};
